import {AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild} from '@angular/core';
import {AsyncPipe, NgIf} from '@angular/common';
import {CdkDrag, CdkDragHandle} from '@angular/cdk/drag-drop';
import {combineLatest, distinctUntilChanged, interval, map, Observable, startWith, Subscription} from 'rxjs';
import {CallService, CallState} from '../call.service';
import {CallOverlayPosition} from '../call-overlay-position';

interface CallBarView {
  state: CallState;
  label: string;
}

/**
 * The call bar (ITP-E-170-01): the other party, the call's duration and End call. When a ring ends unanswered
 * it becomes the outcome card (ITP-E-170-02): "Declined" or "No answer" with Call again and Send a message for
 * the caller, "Call ended" for a callee whose accept came too late. ITP-E-170-04 adds mute, the microphone and
 * speaker pickers, a 3-bar signal indicator, Camera / Record / Open in Meet as placeholders for later slices,
 * and the "Your microphone is blocked" banner. ITP-E-172-02 makes Record the host's request to record, which
 * starts only once everyone in the call allows it. ITP-E-172-03 turns on Camera, in the same session, and the bar
 * grows a small video view of whoever has video on; and Open in Meet, which hands the call over to the Meet room.
 * ITP-E-175-01: a tab that does not hold the call shows "On a call in another tab · mm:ss" with End call, nothing else.
 * ITP-E-175-02 adds Switch here, which moves the call into this tab; the tab it left says "Call moved to another tab".
 * ITP-E-175-04: the passive bar is a polite status that never takes the focus; Switch here from the keyboard keeps it,
 * on the call bar's End call once the call moves here, or back on Switch here if another tab won the move.
 * ITP-E-180-02: no timer until someone else is in the call — "Ringing…" (1:1) or "Waiting for others…" (group) — and
 * then the timer counts from the first of them to join, as WhatsApp's does.
 * ITP-E-180-07: the bar can be moved — dragged by its name, or with the arrow keys on it — and stays where it was put,
 * on screen, across reloads; a double-click on the name puts it back.
 * ITP-E-180-03: the red button says End call, as WhatsApp's does.
 * ITP-E-180-09: a 1:1 caller's bar says "Calling…" until a tab of the callee shows the ring, then "Ringing…"; "Offline"
 * (· we've emailed them) when no ITP tab of theirs is open.
 * ITP-E-182-01: once the call is a video call, a tab that holds it shows the video call card instead — the bar and its
 * video view are gone, one overlay at a time; Camera here is what converts it. A passive tab keeps its passive bar.
 * ITP-E-182-03: Share beside Camera shares this side's screen in the same session, and converts the call the same way —
 * the card takes over and gives the screen the big space. While someone else shares it asks to take over first, and a
 * share that cannot start says why.
 * ITP-E-182-04: the bar is the only voice overlay — the group call panel is gone. In a group call a Participants toggle
 * opens the list of everyone with where they stand, above the bar, moving with it. Like WhatsApp, the red button is End
 * call in every call: in a group call it takes only this side out, in a 1:1 it ends it for both. The host's End call for
 * everyone is in the ⋯ menu, behind a confirm.
 * ITP-E-182-05: Open in Meet ↗ moved into the ⋯ menu, for anyone in the call — the same hand-over to the Meet room in a
 * new tab; the Participants list is the shared one the video call card shows too.
 * ITP-E-185-03 (D-11): the voice call itself is no longer drawn here. A call this tab holds — ringing out, joining or in
 * its media — is the shared compact call view's, drawn by the video call card's shell: the voice strip (the name and the
 * timer, Mute, Camera, ⋯ and End call; Participants, Devices, Share, Record, Open in Meet and End for everyone in ⋯),
 * which turns into the card on Camera or Share without being drawn again. The strip sits, moves and is remembered where
 * this bar does. The bar keeps its shell — the drag and the remembered place — for what is not a held call: the blocked
 * microphone, "moved the call to Meet", the outcome card and the passive bar of a call another tab holds, with Switch here.
 * Mounted beside the router outlet, so it stays while the user navigates.
 */
@Component({
  selector: 'app-call-bar',
  standalone: true,
  imports: [NgIf, AsyncPipe, CdkDrag, CdkDragHandle],
  templateUrl: './call-bar.component.html',
  styleUrls: ['./call-bar.component.scss']
})
export class CallBarComponent implements AfterViewInit, OnDestroy {
  /** Where the bar was moved to, remembered across reloads (ITP-E-180-07); the voice strip's place too (ITP-E-185-03). */
  static readonly positionKey = 'itp.calls.callBar.position';

  /** The End call of the call this tab holds: the compact call view's (ITP-E-185-03). */
  static readonly heldHangUp = 'app-video-call-card .compact-call-view-hang-up';

  readonly view$: Observable<CallBarView> = combineLatest([
    this.callService.state$,
    interval(1000).pipe(startWith(0))
  ]).pipe(map(([state]) => ({state, label: CallBarComponent.label(state, Date.now())})));

  readonly overlay = new CallOverlayPosition(CallBarComponent.positionKey);

  /** Switch here was pressed from the keyboard focus: the focus follows to whichever bar the move leaves. */
  private focusFollowsSwitch = false;
  /** The bar takes its place back as the voice strip, which shares it, left it (ITP-E-185-03). */
  private readonly strip: Subscription;

  /** Everything the bar shows, moved as one (ITP-E-180-07). */
  @ViewChild('overlayElement', {static: true})
  private overlayElement!: ElementRef<HTMLElement>;

  /** The passive bar's Switch here, created again when another tab won the move. */
  @ViewChild('switchHereButton')
  set switchHereButton(button: ElementRef<HTMLButtonElement> | undefined) {
    if (this.followFocus(button?.nativeElement)) {
      this.focusFollowsSwitch = false;
    }
  }

  constructor(private callService: CallService, private zone: NgZone) {
    this.strip = this.callService.state$.pipe(map(state => CallService.showsCallView(state)), distinctUntilChanged())
      .subscribe(held => {
        if (!held) {
          this.overlay.refresh();
        }
      });
  }

  ngAfterViewInit(): void {
    this.overlay.attach(this.overlayElement.nativeElement, this.zone);
  }

  ngOnDestroy(): void {
    this.strip.unsubscribe();
    this.overlay.detach();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.overlay.keepInView();
  }

  /** Follow whoever moved the call to Meet, once the call has ended here. */
  onFollowToMeet(meetingId: string): void {
    this.callService.dismissMovedToMeet();
    window.open(CallService.meetUrl(meetingId), '_blank', 'noopener');
  }

  onDismissMovedToMeet(): void {
    this.callService.dismissMovedToMeet();
  }

  /**
   * The red button's tooltip. It is End call in every call, as WhatsApp's is (ITP-E-182-04); a group call goes on without
   * whoever ends it, and the tooltip says so.
   */
  static hangUpTitle(state: CallState): string {
    return state.isGroup ? 'End call (the others stay in the call)' : 'End call';
  }

  hangUpTitle(state: CallState): string {
    return CallBarComponent.hangUpTitle(state);
  }

  /** The passive bar's End call (ITP-E-175-01): it ends the call another tab holds. */
  onHangUp(): void {
    this.callService.hangUp().then();
  }

  /**
   * Moves the call from the tab that holds it into this one (ITP-E-175-02). Pressed from the keyboard, the focus follows:
   * to the voice strip's End call once the call is here (ITP-E-185-03), or back to Switch here if another tab won it.
   */
  onSwitchHere(): void {
    this.focusFollowsSwitch = !!document.activeElement?.closest('.call-elsewhere');
    this.callService.switchHere().then(() => {
      if (this.callService.state.phase === 'elsewhere') {
        return;
      }
      const follows = this.focusFollowsSwitch;
      this.focusFollowsSwitch = false; // the move is over: later bars never take the focus
      if (follows) {
        // The strip that holds the call now is the compact call view's, drawn once the change has been rendered.
        setTimeout(() => this.followFocus(document.querySelector<HTMLElement>(CallBarComponent.heldHangUp) ?? undefined, true));
      }
    });
  }

  onCallAgain(): void {
    this.callService.callAgain().then();
  }

  onSendMessage(): void {
    this.callService.sendMessage();
  }

  onDismiss(): void {
    this.callService.dismissOutcome();
  }

  onDismissMicBlocked(): void {
    this.callService.dismissMicBlocked();
  }

  /**
   * Moves the focus to `button` only while it follows a Switch here, and only from nowhere: the button pressed went
   * with its bar, so the focus fell to the page. Focus the user put anywhere else stays there.
   */
  private followFocus(button: HTMLElement | undefined, follows = this.focusFollowsSwitch): boolean {
    const active = document.activeElement;
    if (!button || !follows || (active && active !== document.body)) {
      return false;
    }
    button.focus();
    return true;
  }

  static label(state: CallState, now: number): string {
    switch (state.phase) {
      case 'outgoing':
        return CallBarComponent.ringing(state);
      case 'joining':
        return 'Connecting…';
      case 'connected':
        // ITP-E-170-06: either side dropped out of the media; the call ends if it isn't back in 30 seconds.
        if (state.reconnecting) {
          return 'Reconnecting…';
        }
        // ITP-E-180-02: no timer until someone else is in the call; it counts from the first of them to join.
        return state.connectedAt ? CallBarComponent.duration(now - state.connectedAt) : CallBarComponent.waiting(state);
      case 'elsewhere':
        // ITP-E-175-01: another tab holds the call; the timer counts from when that tab joined.
        return CallBarComponent.duration(now - (state.connectedAt ?? now));
      case 'ended':
        // ITP-E-182-06: the outcome names the kind of call — "Video call · No answer", "Voice call · Declined".
        return state.outcome ? CallService.outcomeLabel(state.outcome, state.mode) : '';
      default:
        return '';
    }
  }

  /**
   * What a 1:1 caller's bar says while it rings (ITP-E-180-09): "Ringing…" only once a tab of the callee shows the ring,
   * "Offline" when no ITP tab of theirs is open — "Offline · we've emailed them" when the email went — and "Calling…"
   * before either is known.
   */
  static ringing(state: CallState): string {
    if (state.invitationId && state.ring === 'ringing') {
      return 'Ringing…';
    }
    if (state.invitationId && state.ring === 'offline') {
      return state.emailed ? 'Offline · we\'ve emailed them' : 'Offline';
    }
    return 'Calling…';
  }

  /**
   * What a connected bar says while nobody else is in the media yet (ITP-E-180-02): a group call waits for the others, a
   * 1:1 caller hears it ring, and a 1:1 callee, who answered, waits for the caller's audio.
   */
  static waiting(state: CallState): string {
    if (state.isGroup) {
      return 'Waiting for others…';
    }
    return state.isCaller ? 'Ringing…' : 'Connecting…';
  }

  static duration(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
